import {ChevronDownIcon, ShieldIcon, SyncIcon} from '@primer/octicons-react'
import {
  DeferredType,
  type CompletedReview,
  type CopilotReviewerRow,
  type PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import styles from './Reviewers.module.css'
import {pullRequestsLayoutRoute} from '../../../routes/layout-route'
import {pullRequestsChangesWithRangeRoute} from '../../../routes/files-changed-routes'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Banner, IconButton} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {clsx} from 'clsx'
import {useCallback, useRef, useState, type RefObject} from 'react'
import {UserSectionSkeleton} from '../UserSectionSkeleton'
import {Link} from '@github-ui/react-router'
import {isAutomatedReviewBot, ReviewerIdentity, reviewerDisplayName} from './ReviewerIdentity'
import {reviewState, reviewStateTooltip, deferredTooltip, codeOwnerTooltip} from './review-state-helpers'
import {useReRequestReviewMutation} from '../../../mutations/use-re-request-review-mutation'
import {useReviewers} from '../data/use-reviewers'
import {announce} from '@github-ui/aria-live'
import {usePullRequestAnalytics} from '../../../hooks/use-pull-request-analytics'
import {CopilotReviewEffortMenu} from './CopilotReviewEffortMenu'
import {copilotReviewStatusText} from './copilot-review-effort'
import {isCopilotPullRequestReviewer, isCopilotReviewer, normalizeReviewerSlug} from './copilot-reviewer'
import {useReviewersSectionAnchorRef} from './ReviewersSection'
import {DeferredStatusIcon, ReviewStatusIndicator} from './ReviewStatusIcons'
import {useStagedCopilotReviewEffort} from './use-staged-copilot-review-effort'
import {CopilotRequestRow} from './rows/CopilotRequestRow'
import {DeferredCopilotReviewRow} from './rows/DeferredCopilotReviewRow'

export function Reviewers() {
  const returnFocusRef = useReviewersSectionAnchorRef()
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)

  return <ReviewersComponent key={`${owner}/${repo}/${pr_number}`} returnFocusRef={returnFocusRef} />
}

export function ReviewersComponent({
  returnFocusRef,
}: {
  returnFocusRef?: RefObject<HTMLButtonElement | null> | null
} = {}) {
  const {data: reviewers, isPending, isError} = useReviewers()
  const [errorMessage, setErrorMessage] = useState<string>()
  const errorBannerRef = useRef<HTMLElement>(null)
  const restoreFocus = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement === document.body) returnFocusRef?.current?.focus()
    })
  }, [returnFocusRef])

  if (isPending && !reviewers) return <UserSectionSkeleton placeholderRows={2} />
  if (isError && !reviewers) return <span className={styles.emptyState}>Unable to load reviewers</span>

  const completedReviews = reviewers?.completedReviews ?? []
  const pendingRequests = reviewers?.pendingReviewRequests ?? []
  const copilotReviewerRow = reviewers?.copilotReviewerRow
  const awaitingInitialCopilotRequest =
    !!copilotReviewerRow?.requestable &&
    !pendingRequests.some(request => matchesCopilotReviewerRow(request.reviewer, copilotReviewerRow)) &&
    !completedReviews.some(review => matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow))

  if (completedReviews.length === 0 && pendingRequests.length === 0 && !awaitingInitialCopilotRequest) {
    return <span className={styles.emptyState}>No reviewers</span>
  }

  const writeAccessReviews = completedReviews.filter(review => review.hasWriteAccess || isBotReviewer(review.reviewer))
  const nonWriteAccessReviews = completedReviews.filter(
    review => !review.hasWriteAccess && !isBotReviewer(review.reviewer),
  )
  const copilotPendingRequest = pendingRequests.find(
    request =>
      isCopilotPullRequestReviewer(request.reviewer) && matchesCopilotReviewerRow(request.reviewer, copilotReviewerRow),
  )
  const visibleWriteAccessReviews = writeAccessReviews.filter(
    review => !copilotPendingRequest || !matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow),
  )

  const clearMutationError = () => {
    setErrorMessage(undefined)
  }
  const focusErrorBanner = (origin: Element | null) => {
    requestAnimationFrame(() => {
      const active = document.activeElement
      if (active === document.body || active === origin) {
        errorBannerRef.current?.focus()
      }
    })
  }
  const mutationSuccess = (message: string) => {
    announce(message)
    restoreFocus()
  }
  const copilotRequestMutationError = (origin: Element | null) => {
    setErrorMessage('Failed to request review from Copilot.')
    focusErrorBanner(origin)
  }
  const reviewerMutationError = (reviewerName: string | undefined) => (origin: Element | null) => {
    setErrorMessage(reviewerName ? `Failed to re-request review from ${reviewerName}.` : 'Failed to re-request review.')
    focusErrorBanner(origin)
  }
  const reviewerMutationSuccess = (reviewerName: string | undefined) => () =>
    mutationSuccess(`Review requested from ${reviewerName ?? 'reviewer'}`)

  return (
    <div>
      <ul className={styles.reviewerList}>
        {visibleWriteAccessReviews.map(review => {
          const matchesCopilotRow = matchesCopilotReviewerRow(review.reviewer, copilotReviewerRow)
          const reviewerName = review.reviewer ? reviewerDisplayName(review.reviewer) : 'reviewer'
          return (
            <CompletedReviewRow
              key={`completed-${review.id}`}
              review={review}
              muted={isBotReviewer(review.reviewer)}
              copilotReviewerRow={matchesCopilotRow ? copilotReviewerRow : undefined}
              onMutationStart={clearMutationError}
              onMutationError={reviewerMutationError(reviewerName)}
              onMutationSuccess={reviewerMutationSuccess(reviewerName)}
              onCloseWithoutTarget={restoreFocus}
            />
          )
        })}
        {pendingRequests.map(request => {
          const matchesCopilotRow = matchesCopilotReviewerRow(request.reviewer, copilotReviewerRow)
          if (matchesCopilotRow && copilotReviewerRow && request.deferredType === DeferredType.COPILOT) {
            const reviewerName = request.reviewer ? reviewerDisplayName(request.reviewer) : 'Copilot'
            return (
              <DeferredCopilotReviewRow
                key={`pending-${request.reviewer?.id}`}
                request={request}
                row={copilotReviewerRow}
                onMutationStart={clearMutationError}
                onMutationError={copilotRequestMutationError}
                onMutationSuccess={() => mutationSuccess(`Review requested from ${reviewerName}`)}
                onCloseWithoutTarget={restoreFocus}
              />
            )
          }

          return (
            <PendingReviewRow
              key={`pending-${request.reviewer?.id}`}
              request={request}
              copilotReviewerRow={matchesCopilotRow ? copilotReviewerRow : undefined}
              lastCompletedCopilotReviewEffort={
                matchesCopilotRow ? copilotReviewerRow?.lastExecutedReviewEffort : undefined
              }
            />
          )
        })}
        {awaitingInitialCopilotRequest && copilotReviewerRow ? (
          <CopilotRequestRow
            row={copilotReviewerRow}
            onMutationStart={clearMutationError}
            onMutationError={copilotRequestMutationError}
            onMutationSuccess={() =>
              mutationSuccess(
                `Review requested from ${copilotReviewerRow.reviewer ? reviewerDisplayName(copilotReviewerRow.reviewer) : 'Copilot'}`,
              )
            }
            onCloseWithoutTarget={restoreFocus}
          />
        ) : null}
      </ul>
      {errorMessage ? (
        <Banner ref={errorBannerRef} variant="critical" className={styles.mutationError} role="alert">
          <Banner.Title as="h5">{errorMessage}</Banner.Title>
        </Banner>
      ) : null}
      {nonWriteAccessReviews.length > 0 && (
        <NonWriteAccessReviewers
          reviews={nonWriteAccessReviews}
          hasWriteAccessReviews={visibleWriteAccessReviews.length > 0}
          onMutationStart={clearMutationError}
          onMutationError={reviewerMutationError}
          onMutationSuccess={reviewerMutationSuccess}
          onCloseWithoutTarget={restoreFocus}
        />
      )}
    </div>
  )
}

function isBotReviewer(reviewer: CompletedReview['reviewer']): boolean {
  return isCopilotReviewer(reviewer) || isAutomatedReviewBot(reviewer)
}

function NonWriteAccessReviewers({
  reviews,
  hasWriteAccessReviews,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  reviews: CompletedReview[]
  hasWriteAccessReviews: boolean
  onMutationStart: () => void
  onMutationError: (reviewerName: string | undefined) => (origin: Element | null) => void
  onMutationSuccess: (reviewerName: string | undefined) => () => void
  onCloseWithoutTarget?: () => void
}) {
  const [isExpanded, setIsExpanded] = useState(true)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const toggle = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.reviewers_toggle', 'OVERVIEW_REVIEWER', {is_open: !isExpanded})
    setIsExpanded(previous => !previous)
  }, [isExpanded, sendPullRequestAnalyticsEvent])

  const count = reviews.length
  const label = `${hasWriteAccessReviews ? '+' : ''}${count} more ${count === 1 ? 'reviewer' : 'reviewers'}`

  return (
    <div className={styles.nonWriteAccessGroup}>
      <Tooltip text="Reviewers whose approvals may not affect merge requirements">
        <button type="button" className={styles.moreReviewersToggle} aria-expanded={isExpanded} onClick={toggle}>
          <span>{label}</span>
          <ChevronDownIcon
            size={16}
            className={clsx(styles.moreReviewersChevron, isExpanded && styles.moreReviewersChevronExpanded)}
          />
        </button>
      </Tooltip>
      {isExpanded && (
        <ul className={clsx(styles.reviewerList, 'pl-3')}>
          {reviews.map(review => (
            <CompletedReviewRow
              key={`completed-${review.id}`}
              review={review}
              muted
              onMutationStart={onMutationStart}
              onMutationError={onMutationError(review.reviewer ? reviewerDisplayName(review.reviewer) : undefined)}
              onMutationSuccess={onMutationSuccess(review.reviewer ? reviewerDisplayName(review.reviewer) : undefined)}
              onCloseWithoutTarget={onCloseWithoutTarget}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function CompletedReviewRow({
  review,
  muted = false,
  copilotReviewerRow,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  review: CompletedReview
  muted?: boolean
  copilotReviewerRow?: CopilotReviewerRow
  onMutationStart?: () => void
  onMutationError?: (origin: Element | null) => void
  onMutationSuccess?: () => void
  onCloseWithoutTarget?: () => void
}) {
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const mutationOriginRef = useRef<Element | null>(null)
  const {mutate: reRequestReview, isPending} = useReRequestReviewMutation({
    onError: () => onMutationError?.(mutationOriginRef.current),
    onSuccess: onMutationSuccess,
  })
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const canReRequestReview = review.canReRequest && (copilotReviewerRow?.requestable ?? true)
  const {shownEffort, explicitEffort, stageEffort} = useStagedCopilotReviewEffort(
    copilotReviewerRow,
    canReRequestReview,
  )
  const reviewer = review.reviewer
  if (!reviewer) return null

  const name = reviewerDisplayName(reviewer)
  const {icon: stateIcon, className: stateClass} = reviewState(review.state)
  const changesWithRangePath = pullRequestsChangesWithRangeRoute.generatePath({
    owner,
    repo,
    pr_number,
    range: `BASE..${review.headSha}`,
  })
  const requestReview = () => {
    if (isPending) return
    mutationOriginRef.current = document.activeElement
    onMutationStart?.()
    announce(`Re-requesting review from ${name}`)
    reRequestReview({
      reviewerId: Number(reviewer.id),
      ...(explicitEffort ? {copilotReviewEffort: explicitEffort} : {}),
    })
  }

  return (
    <li className={clsx(styles.reviewerRow, copilotReviewerRow && styles.withEffortMenu)}>
      <span className={styles.reviewerIdentityGroup}>
        <ReviewerIdentity reviewer={reviewer} />
        {copilotReviewerRow ? (
          <span className={styles.effortMenu}>
            <CopilotReviewEffortMenu
              row={copilotReviewerRow}
              selectedEffort={shownEffort}
              disabled={isPending || !canReRequestReview}
              onCloseWithoutTarget={onCloseWithoutTarget}
              onSelectEffort={effort => {
                sendPullRequestAnalyticsEvent('overview.copilot_review_effort_select', 'OVERVIEW_REVIEWER', {
                  review_effort: effort,
                })
                stageEffort(effort)
              }}
            />
          </span>
        ) : null}
      </span>
      <span className={styles.indicators}>
        {canReRequestReview && (
          <IconButton
            icon={SyncIcon}
            aria-label={`Re-request review from ${name}`}
            variant="invisible"
            size="small"
            inactive={isPending || undefined}
            aria-disabled={isPending || undefined}
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
            onClick={() => {
              if (isPending) return
              sendPullRequestAnalyticsEvent('overview.re_request_review', 'OVERVIEW_REVIEWER')
              requestReview()
            }}
          />
        )}
        <IconButton
          as={Link}
          to={changesWithRangePath}
          icon={stateIcon}
          aria-label={reviewStateTooltip(review.state, name)}
          variant="invisible"
          size="small"
          tooltipDirection="nw"
          className={clsx(styles.statusIcon, muted ? 'color-fg-muted' : stateClass)}
        />
      </span>
    </li>
  )
}

function PendingReviewRow({
  request,
  copilotReviewerRow,
  lastCompletedCopilotReviewEffort,
}: {
  request: PendingReviewRequest
  copilotReviewerRow?: CopilotReviewerRow
  lastCompletedCopilotReviewEffort?: number
}) {
  const reviewer = request.reviewer
  if (!reviewer) return null

  const name = reviewerDisplayName(reviewer)
  const activeCopilotRequest = copilotReviewerRow !== undefined && request.deferredType === DeferredType.UNSPECIFIED
  const inProgressLabel = activeCopilotRequest
    ? copilotReviewStatusText({
        row: copilotReviewerRow,
        requestedCopilotReviewEffort: request.requestedCopilotReviewEffort,
        lastCompletedCopilotReviewEffort,
      })
    : undefined

  return (
    <li className={styles.reviewerRow}>
      <ReviewerIdentity reviewer={reviewer} />
      <span className={styles.indicators}>
        {request.deferredType !== DeferredType.UNSPECIFIED && (
          <DeferredStatusIcon label={deferredTooltip(request.deferredType, name)} />
        )}
        {(request.isCodeOwner || request.isCodeOwnerFromDelegation) && (
          <IconButton
            icon={ShieldIcon}
            aria-label={codeOwnerTooltip(request)}
            variant="invisible"
            size="small"
            tooltipDirection="nw"
            className={clsx(styles.statusIcon, 'color-fg-muted')}
          />
        )}
        {request.deferredType !== DeferredType.COPILOT ? (
          <ReviewStatusIndicator label={inProgressLabel ?? `Awaiting requested review from ${name}`} />
        ) : null}
      </span>
    </li>
  )
}

function matchesCopilotReviewerRow(
  reviewer: PendingReviewRequest['reviewer'],
  row: CopilotReviewerRow | undefined,
): boolean {
  const rowReviewer = row?.reviewer
  if (!reviewer || !rowReviewer || reviewer.reviewerType !== rowReviewer.reviewerType) return false

  const matchingId = reviewer.id !== 0n && rowReviewer.id !== 0n && reviewer.id === rowReviewer.id
  const reviewerSlug = normalizeReviewerSlug(reviewer.slug)
  const matchingSlug = reviewerSlug !== '' && reviewerSlug === normalizeReviewerSlug(rowReviewer.slug)
  return matchingId || matchingSlug
}
